import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/* Pantalla "Crear/Recuperar contraseña" (aterrizaje del enlace del email) con el
   look del canal: fondo mint, logos Pilar Domínguez + MEFIR, botón teal, Montserrat,
   medidor de contraseña. La lógica (validar token → reset → login) NO cambia.
   Política de contraseña alineada con el resto: ≥8 · una letra · un número · no típica. */

const TEAL = '#2C6675';
const TEAL2 = '#3C8696';

const S = {
  wrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)',
    fontFamily: "'Montserrat', system-ui, sans-serif", padding: '32px 18px',
  },
  logos: { display: 'flex', alignItems: 'center', gap: 18, marginBottom: 22 },
  card: {
    background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(44,107,118,.16)',
    width: '100%', maxWidth: 440, padding: '30px 28px 28px', boxSizing: 'border-box',
  },
  title: { margin: '0 0 4px', fontSize: 24, color: '#16323a', fontWeight: 800, textAlign: 'center' },
  desc: { margin: '0 0 22px', color: '#33474d', fontSize: 14.5, textAlign: 'center' },
  label: { fontWeight: 700, fontSize: 13, color: '#16323a' },
  input: {
    width: '100%', height: 46, borderRadius: 12, border: '2px solid #9db3ab', padding: '0 14px',
    fontSize: 16, outline: 'none', boxSizing: 'border-box', margin: '6px 0 16px', color: '#16323a',
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  btn: (dis) => ({
    height: 48, borderRadius: 12, border: 'none', width: '100%', cursor: dis ? 'default' : 'pointer',
    fontWeight: 800, fontSize: 15.5, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: dis ? '#cfe0e1' : `linear-gradient(135deg,${TEAL2},${TEAL})`, color: '#fff',
  }),
  ghost: {
    height: 46, borderRadius: 12, width: '100%', cursor: 'pointer', marginTop: 10,
    fontWeight: 600, fontSize: 14.5, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: 'rgba(60,134,150,.10)', color: TEAL, border: '1px solid rgba(60,134,150,.35)',
  },
  spin: {
    width: 30, height: 30, borderRadius: '50%', border: '3px solid #d9e8e7', borderTopColor: TEAL,
    margin: '4px auto 14px', animation: 'rp-spin 0.9s linear infinite',
  },
};

const Logos = () => (
  <div style={S.logos}>
    <img src="/logo-pd.png" alt="Pilar Domínguez" style={{ height: 38, width: 'auto' }} />
    <span style={{ width: 1, height: 32, background: 'rgba(44,107,118,.3)' }} />
    <img src="/logo-mefir-trans.png" alt="Instituto MEFIR" style={{ height: 34, width: 'auto' }} />
  </div>
);

const _debiles = /^(?:1234\d*|12345678|123456789|0000+|1111+|password|passw0rd|contrasena|contraseña|qwerty|abcdef|admin\d*)$/i;

// OPERATIVA HUMANA (Ann 2026-07-16). Casos reales de hoy: clientas que abren el
// correo "por curiosidad en el metro", lo dejan para hacerlo con calma en casa y
// al día siguiente el enlace ya está muerto; y clientas que ya crearon la
// contraseña y vuelven a pinchar el MISMO enlace (todos los correos llevan el
// mismo asunto, no los distinguen) y reciben "enlace no válido".
// Antes: aviso fugaz + `navigate('/login')` → callejón sin salida, porque muchas
// nunca llegaron a tener contraseña con la que iniciar sesión. Ann: "los avisos
// no les dan alternativas" · "ayuda a la gente a entender qué pasa".
// Ahora: se les explica y se les da salida AQUÍ (pedirse otro enlace solas).
// El login de clienta es `/entrar` (canal.*), NO `/login` (eso es staff en crm.*).
const rutaLogin = () => {
  try {
    if ((window.location.hostname || '').toLowerCase().startsWith('canal.')) return '/entrar';
  } catch (e) { /* entorno raro */ }
  return '/login';
};

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const token = searchParams.get('token');
  // Salida de autoservicio cuando el enlace ya no sirve (caducado o ya usado).
  const [reenvioEmail, setReenvioEmail] = useState('');
  const [reenviando, setReenviando] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [reenvioAviso, setReenvioAviso] = useState('');
  const [masTarde, setMasTarde] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);   // sin token → pantalla con explicación y salida
      return;
    }
    const validateToken = async () => {
      try {
        await axios.get(`${API}/auth/validate-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        // Ni aviso fugaz ni redirección: la pantalla de abajo se lo explica
        // y le ofrece pedirse otro enlace sin llamar a nadie.
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const pedirEnlaceNuevo = async () => {
    const email = (reenvioEmail || '').trim();
    if (!email || !email.includes('@')) {
      setReenvioAviso(t('reset.pideEmailValido'));
      return;
    }
    setReenviando(true);
    setReenvioAviso('');
    let limite = false;
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
    } catch (error) {
      // El backend responde igual exista o no la cuenta (no filtra cuentas).
      // PERO el límite (429) sí hay que decirlo para que no insista.
      if (error?.response?.status === 429) limite = true;
    } finally {
      setReenviando(false);
      if (limite) setReenvioAviso(t('comun.demasiadasSolicitudes'));
      else setReenviado(true);
    }
  };

  const reglasPw = [
    { ok: newPassword.length >= 8, txt: t('comun.reglaMin8') },
    { ok: /[a-zA-Z]/.test(newPassword), txt: t('comun.reglaLetra') },
    { ok: /\d/.test(newPassword), txt: t('comun.reglaNumero') },
    { ok: newPassword.length > 0 && !_debiles.test(newPassword), txt: t('comun.reglaTipica') },
  ];
  const pwFuerte = reglasPw.every(r => r.ok);
  const pwOk = pwFuerte && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwFuerte) {
      toast.error(t('reset.pwDebil'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('reset.noCoinciden'));
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      // Auto-login (UX Ann 2026-07-13): al Guardar, entra directa (no vuelve al login).
      if (resp?.data?.access_token) {
        localStorage.setItem('token', resp.data.access_token);
        toast.success(t('reset.creadaEntrando'));
        setTimeout(() => { window.location.href = '/'; }, 600);
      } else {
        toast.success(t('reset.creadaOk'));
        setTimeout(() => navigate(rutaLogin()), 1500);  // /login NO existe en canal.* → allí es /entrar
      }
    } catch (error) {
      // 429 → decir "espera unos minutos" (que no insista · Ann 2026-08-01);
      // el resto, mensaje genérico (nunca el detail crudo del backend, fuga CA).
      if (error?.response?.status === 429) toast.error(t('comun.demasiadasSolicitudes'));
      else toast.error(t('reset.errorRestablecer'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div style={S.wrap}>
        <style>{'@keyframes rp-spin{to{transform:rotate(360deg)}}'}</style>
        <Logos />
        <div style={{ ...S.card, textAlign: 'center' }}>
          <div style={S.spin} />
          <p style={{ margin: 0, color: '#33474d', fontSize: 14.5 }}>{t('reset.verificandoEnlace')}</p>
        </div>
      </div>
    );
  }

  // Enlace caducado / ya usado / sin token → EXPLICAR y dar SALIDA (nunca un
  // callejón sin salida: puede pedirse otro enlace ella misma, aquí y ahora).
  if (!tokenValid) {
    return (
      <div style={S.wrap}>
        <Logos />
        <div style={{ ...S.card, textAlign: 'center' }}>
          <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(217,83,79,.10)', display: 'grid', placeItems: 'center', margin: '2px auto 14px' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#d9534f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
          </div>

          {reenviado ? (
            <>
              <h1 style={S.title}>{t('reset.nuevoEnviadoTitulo')}</h1>
              <p style={{ ...S.desc, marginBottom: 8 }}>{t('reset.nuevoEnviadoTexto')}</p>
              <p style={{ ...S.desc, fontSize: 13.5, marginBottom: 0 }}>{t('reset.nuevoEnviadoSpam')}</p>
            </>
          ) : (
            <>
              <h1 style={S.title}>{t('reset.caducadoTitulo')}</h1>
              <p style={{ ...S.desc, marginBottom: 8 }}>{t('reset.caducadoPorque')}</p>
              <p style={{ ...S.desc, marginBottom: 18 }}>{t('reset.caducadoTranquila')}</p>

              <div style={{ textAlign: 'left', background: '#f2f7f5', border: '1px solid #d8e5e0', borderRadius: 12, padding: '14px' }}>
                <label htmlFor="rp-mail" style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: '#16323a', marginBottom: 7 }}>
                  {t('reset.caducadoPideNuevo')}
                </label>
                <input
                  id="rp-mail" type="email" autoComplete="email" value={reenvioEmail}
                  onChange={(e) => setReenvioEmail(e.target.value)}
                  placeholder={t('reset.tuCorreo')}
                  style={{ ...S.input, margin: '0 0 4px' }}
                />
                {reenvioAviso && (
                  <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600, color: '#8a1f14' }}>{reenvioAviso}</p>
                )}
                <button style={{ ...S.btn(reenviando), marginTop: 10 }} onClick={pedirEnlaceNuevo} disabled={reenviando}>
                  {reenviando ? t('reset.enviando') : t('reset.enviarmeNuevo')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <style>{'input::placeholder{color:#6b7d78;opacity:1}'}</style>
      <Logos />
      <div style={S.card}>
        <h1 style={S.title}>{t('reset.creaTitulo')}</h1>
        <p style={S.desc}>{t('reset.creaSub')}</p>

        {/* AVISO DE CADUCIDAD (Ann 2026-07-16): que sepa ANTES de dejarlo a medias
            que el enlace muere en 24 h. Es la trampa nº1: lo abren "de paso" y lo
            posponen para hacerlo con calma; al volver, ya no sirve. */}
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: '#fdf6ea', border: '1px solid #e8cf9e', borderRadius: 12, padding: '11px 13px', margin: '0 0 18px' }}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#8a5a20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
          </svg>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: '#5e4415', textAlign: 'left' }}>{t('reset.avisoCaduca24h')}</p>
        </div>

        {/* "Prefiero hacerlo más tarde": no la dejamos con la duda — le contamos
            cómo volver (pedir otro enlace) en vez de que se vaya y se quede fuera. */}
        {masTarde && (
          <div style={{ background: '#f2f7f5', border: '1px solid #d8e5e0', borderRadius: 12, padding: '13px 14px', margin: '0 0 18px', textAlign: 'left' }}>
            <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#16323a' }}>{t('reset.masTardeTitulo')}</p>
            <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.55, color: '#33474d' }}>{t('reset.masTardeTexto')}</p>
            <button type="button" style={{ ...S.ghost, marginTop: 0 }} onClick={() => setMasTarde(false)}>
              {t('reset.masTardeVolver')}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={S.label}>{t('reset.nuevaPassword')}</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('comun.min8Placeholder')} disabled={loading} style={S.input} />

          <div style={{ margin: '-6px 0 16px', padding: '12px 14px', background: '#f2f8f5', border: '1px solid #cfe0da', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#16323a' }}>{t('reset.debeTener')}</span>
            {reglasPw.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: r.ok ? '#1c6b45' : '#334', lineHeight: 1.4 }}>
                <span style={{ width: 17, height: 17, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: r.ok ? '#1c6b45' : '#fff', border: r.ok ? 'none' : '2px solid #9db3ab', color: '#fff', fontSize: 11 }}>{r.ok ? '✓' : ''}</span>
                {r.txt}
              </div>
            ))}
          </div>

          <label style={S.label}>{t('comun.repitePassword')}</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('comun.repitePlaceholder')} disabled={loading} style={S.input} />
          {confirmPassword && newPassword !== confirmPassword && (
            <p style={{ color: '#d9534f', fontSize: 12.5, margin: '-10px 0 12px' }}>{t('comun.noCoinciden')}</p>
          )}

          <button type="submit" style={S.btn(loading || !pwOk)} disabled={loading || !pwOk}>
            {loading ? t('comun.guardando') : t('comun.guardarPassword')}
          </button>
          {/* Antes: "Volver al login" → la mandaba a una puerta que NO puede abrir
              (muchas no tienen contraseña todavía; ése era el callejón sin salida).
              Ahora: le explicamos cómo volver cuando pueda hacerlo con calma. */}
          {!masTarde && (
            <button type="button" style={S.ghost} onClick={() => setMasTarde(true)} disabled={loading}>
              {t('reset.masTarde')}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
